import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import {
  featuredNavigationHrefs,
  getNavigationItemByRoute,
  isRouteActive,
  navigationItems,
  primaryNavigationHrefs,
  supportingNavigationHrefs
} from "../../data/navigation/navigation";
import { useClock } from "../../hooks/useClock";
import { t } from "../../lib/i18n";
import { useSettings } from "../settings/SettingsProvider";
import type { Locale } from "../../types/app";
import { Button } from "../common/Button";
import { Icon } from "../common/Icon";
import { MobileMenu } from "./MobileMenu";
import { NavIcon } from "./NavIcon";

interface NavbarProps {
  readonly route: string;
  readonly locale: Locale;
  readonly onOpenSettings: () => void;
}

export function Navbar({ route, locale, onOpenSettings }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [morePanelStyle, setMorePanelStyle] = useState<CSSProperties | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const morePanelRef = useRef<HTMLDivElement>(null);
  const { settings } = useSettings();
  const clock = useClock(locale, settings.hijriOffset);
  const moreMenuId = useId();
  const mobileMenuId = useId();
  const isArabic = locale === "ar";
  const currentItem = getNavigationItemByRoute(route);
  const compactTime = new Intl.DateTimeFormat(isArabic ? "ar-OM" : "en-US", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(clock.now);

  const primaryItems = navigationItems.filter((item) =>
    primaryNavigationHrefs.includes(item.href as (typeof primaryNavigationHrefs)[number])
  );
  const featuredItems = navigationItems.filter((item) =>
    featuredNavigationHrefs.includes(item.href as (typeof featuredNavigationHrefs)[number])
  );
  const supportingItems = navigationItems.filter((item) =>
    supportingNavigationHrefs.includes(item.href as (typeof supportingNavigationHrefs)[number])
  );

  useEffect(() => {
    setMoreOpen(false);
  }, [route]);

  useEffect(() => {
    if (!moreOpen) return undefined;

    const updateMorePosition = () => {
      const anchor = moreRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const headerRect = anchor.closest(".navbar-premium")?.getBoundingClientRect();
      const sidePadding = 12;
      const width = Math.min(448, window.innerWidth - sidePadding * 2);
      const preferredLeft = isArabic ? rect.right - width : rect.left;
      const left = Math.min(Math.max(preferredLeft, sidePadding), window.innerWidth - width - sidePadding);
      const top = Math.max(rect.bottom + 10, (headerRect?.bottom ?? 0) + 10);
      const maxHeight = Math.max(240, window.innerHeight - top - sidePadding);

      setMorePanelStyle({
        position: "fixed",
        inset: "auto",
        top,
        left,
        width,
        maxWidth: `calc(100vw - ${sidePadding * 2}px)`,
        maxHeight
      });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!moreRef.current?.contains(target) && !morePanelRef.current?.contains(target)) {
        setMoreOpen(false);
      }
    };
    const handleViewportChange = () => updateMorePosition();

    updateMorePosition();
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    requestAnimationFrame(() => morePanelRef.current?.querySelector<HTMLAnchorElement>('a[href]')?.focus());

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isArabic, moreOpen]);

  useEffect(() => {
    if (moreOpen) return;
    setMorePanelStyle(null);
  }, [moreOpen]);

  const morePanel = moreOpen && morePanelStyle ? createPortal(
    <div className="navbar-more__overlay" role="presentation">
      <button
        type="button"
        className="navbar-more__scrim"
        aria-label={isArabic ? "إغلاق قائمة المزيد" : "Close more menu"}
        onClick={() => setMoreOpen(false)}
      />
      <div
        ref={morePanelRef}
        id={moreMenuId}
        className="navbar-more__panel"
        style={morePanelStyle}
        role="menu"
        aria-label={isArabic ? "المزيد من الصفحات" : "More pages"}
      >
        <div className="navbar-more__section">
          <p>{isArabic ? "أدواتك" : "Your tools"}</p>
          <div className="navbar-more__links">
            {featuredItems.map((item) => {
              const active = isRouteActive(route, item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className={active ? "active" : ""}
                  onClick={() => setMoreOpen(false)}
                  role="menuitem"
                  aria-current={active ? "page" : undefined}
                >
                  <NavIcon name={item.icon} />
                  <span>{locale === "ar" ? item.labelAr : item.labelEn}</span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="navbar-more__section">
          <p>{isArabic ? "محتوى إضافي" : "Additional pages"}</p>
          <div className="navbar-more__links">
            {supportingItems.map((item) => {
              const active = isRouteActive(route, item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className={active ? "active" : ""}
                  onClick={() => setMoreOpen(false)}
                  role="menuitem"
                  aria-current={active ? "page" : undefined}
                >
                  <NavIcon name={item.icon} />
                  <span>{locale === "ar" ? item.labelAr : item.labelEn}</span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <header className="navbar navbar-premium" dir={isArabic ? "rtl" : "ltr"} lang={locale}>
        <div className="navbar__inner">
          <div className="brand">
            <a className="brand__home-link" href="#/" aria-label={isArabic ? "أذكاري الرئيسية" : "Athkari home"}>
              <span className="brand__logo-shell" aria-hidden="true">
                <img className="brand__wordmark" src="/assets/icons/athkari-wordmark.svg" alt="" />
              </span>
            </a>
            <span className="brand__copy" aria-hidden="true">
              <strong>{t(locale, "appName")}</strong>
              <small>{t(locale, "appTagline")}</small>
            </span>
          </div>

          <nav className="desktop-nav desktop-nav--primary" aria-label={isArabic ? "التنقل الرئيسي" : "Main navigation"}>
            {primaryItems.map((item) => {
              const active = isRouteActive(route, item.href);
              return (
                <a key={item.href} href={item.href} className={active ? "active" : ""} aria-current={active ? "page" : undefined}>
                  <NavIcon name={item.icon} />
                  <span>{locale === "ar" ? item.labelAr : item.labelEn}</span>
                </a>
              );
            })}
          </nav>

          <div className="navbar-current" aria-live="polite">
            <span>{isArabic ? "الصفحة الحالية" : "Current page"}</span>
            <strong>{currentItem ? (isArabic ? currentItem.labelAr : currentItem.labelEn) : (isArabic ? "أذكاري" : "Athkari")}</strong>
          </div>

          <div className="navbar__actions">
            <div className="navbar-more" ref={moreRef}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={`navbar-more__toggle ${moreOpen ? "is-open" : ""}`}
                aria-expanded={moreOpen}
                aria-controls={moreMenuId}
                aria-haspopup="menu"
                onClick={() => setMoreOpen((value) => !value)}
              >
                <Icon name="sparkles" />
                <span>{isArabic ? "المزيد" : "More"}</span>
                <Icon name="chevron-left" className="icon navbar-more__chevron" />
              </Button>
            </div>

            <time className="navbar-clock" dateTime={clock.now.toISOString()}>
              <strong>{compactTime}</strong>
              <span>{clock.hijri || clock.gregorian}</span>
            </time>

            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="settings-toggle desktop-only"
              onClick={onOpenSettings}
              aria-label={t(locale, "settings")}
            >
              <Icon name="settings" />
            </Button>

            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="mobile-menu-toggle"
              onClick={() => setMenuOpen(true)}
              aria-label={isArabic ? "فتح القائمة" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls={mobileMenuId}
            >
              <Icon name="menu" />
            </Button>
          </div>
        </div>

        <MobileMenu
          menuId={mobileMenuId}
          open={menuOpen}
          route={route}
          locale={locale}
          onClose={() => setMenuOpen(false)}
          onOpenSettings={onOpenSettings}
        />
      </header>
      {morePanel}
    </>
  );
}
